import React, { useState, useEffect } from 'react';
import util from '../../utils/util';
import {
    Card, Button, Select, Table, Tag, Space, Badge,
    message, Row, Col, Statistic, Input, Modal,
    Tooltip, Progress, Alert, Divider, Form
} from 'antd';
import {
    CheckCircleOutlined, CloseCircleOutlined, EyeOutlined,
    FilterOutlined, CheckSquareOutlined
} from '@ant-design/icons';
import MeService from '../../services/MeService';
import moment from 'moment';

const { Option }   = Select;
const { TextArea } = Input;

// ── Constants ─────────────────────────────────────────────────────────────
const PROGRESS_STATUS_CONFIG = {
    not_started:    { color:'default',  label:'Not Started'    },
    in_progress:    { color:'processing',label:'In Progress'   },
    partially_met:  { color:'warning',  label:'Partially Met'  },
    met:            { color:'success',  label:'Met'            },
    not_met:        { color:'error',    label:'Not Met'        },
    completed:      { color:'success',  label:'Completed'      },
    not_applicable: { color:'default',  label:'Not Applicable' },
    delayed:        { color:'error',    label:'Delayed'        },
};

const VERIFY_STATUS_CONFIG = {
    pending:  { color:'orange', label:'Pending',  badge:'warning'  },
    verified: { color:'green',  label:'Verified', badge:'success'  },
    rejected: { color:'red',    label:'Rejected', badge:'error'    },
};

// ── Progress Detail Modal ─────────────────────────────────────────────────
function ProgressDetailModal({ visible, onClose, record, onVerify, onReject }) {
    const [notes,    setNotes]    = useState('');
    const [saving,   setSaving]   = useState(false);

    useEffect(() => {
        if (visible) setNotes('');
    }, [visible]);

    if (!record) return null;

    const ps = PROGRESS_STATUS_CONFIG[record.progress_status] || PROGRESS_STATUS_CONFIG.not_started;
    const vs = VERIFY_STATUS_CONFIG[record.verification_status] || VERIFY_STATUS_CONFIG.pending;

    const handleVerify = async (status) => {
        setSaving(true);
        try {
            await onVerify(record.progress_id, status, notes);
            onClose();
        } finally { setSaving(false); }
    };

    const fields = [
        { label:'Framework',       value: record.framework_name },
        { label:'Indicator',       value: record.indicator_title },
        { label:'Indicator Code',  value: record.indicator_code },
        { label:'Facility',        value: record.facility_name },
        { label:'Period',          value: record.reporting_period },
        { label:'Status',          value: <Badge status={ps.color} text={ps.label}/> },
        { label:'Actual Value',    value: record.actual_value },
        { label:'Target Value',    value: record.target_value },
        { label:'Achievement',     value: record.achievement_pct != null
            ? <Progress percent={parseFloat(record.achievement_pct)} size="small" style={{ width:160 }}/> : null },
        { label:'Reported By',     value: record.reported_by_name },
        { label:'Reported Date',   value: record.reported_date ? moment(record.reported_date).format('DD MMM YYYY') : null },
    ].filter(f => f.value);

    return (
        <Modal
            title={<span style={{ fontWeight:800 }}>🔍 Progress Record — Verification</span>}
            open={visible} onClose={onClose} onCancel={onClose}
            width={700} footer={null}
        >
            {/* Verification status banner */}
            {record.verification_status !== 'pending' && (
                <Alert
                    type={record.verification_status === 'verified' ? 'success' : 'error'}
                    showIcon
                    message={`This record was ${record.verification_status} by ${record.verified_by_name || 'an assessor'} on ${record.verified_date ? moment(record.verified_date).format('DD MMM YYYY') : '—'}`}
                    description={record.verifier_notes}
                    style={{ marginBottom:16 }}
                />
            )}

            {/* Field grid */}
            <Row gutter={[8, 0]}>
                {fields.map(f => (
                    <Col key={f.label} xs={24} sm={12}>
                        <div style={{ display:'flex', gap:8, padding:'6px 0',
                            borderBottom:'1px solid #f0f0f0', fontSize:13 }}>
                            <span style={{ color:'#888', width:120, flexShrink:0 }}>{f.label}</span>
                            <span style={{ fontWeight:600 }}>{f.value}</span>
                        </div>
                    </Col>
                ))}
            </Row>

            {/* Narrative fields */}
            {[
                { label:'Progress Narrative', value: record.progress_narrative },
                { label:'Challenges',         value: record.challenges },
                { label:'Evidence',           value: record.evidence_description },
                { label:'Evidence Reference', value: record.evidence_reference },
                { label:'Gap Identified',     value: record.gap_identified },
                { label:'Corrective Action',  value: record.corrective_action },
                { label:'Lessons Learned',    value: record.lessons_learned },
            ].filter(f => f.value).map(f => (
                <div key={f.label} style={{ marginTop:10 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'#888',
                        textTransform:'uppercase', marginBottom:4 }}>{f.label}</div>
                    <div style={{ fontSize:13, padding:'8px 12px', background:'#f8faff',
                        borderRadius:6, lineHeight:1.6, color:'#333' }}>
                        {f.value}
                    </div>
                </div>
            ))}

            {/* Corrective action status */}
            {record.corrective_action && (
                <div style={{ marginTop:10, display:'flex', gap:12 }}>
                    {record.corrective_action_owner && (
                        <Tag>Owner: {record.corrective_action_owner}</Tag>
                    )}
                    {record.corrective_action_deadline && (
                        <Tag color={moment(record.corrective_action_deadline).isBefore(moment()) ? 'red' : 'blue'}>
                            Deadline: {moment(record.corrective_action_deadline).format('DD MMM YYYY')}
                            {moment(record.corrective_action_deadline).isBefore(moment()) && ' ⚠ OVERDUE'}
                        </Tag>
                    )}
                    {record.corrective_action_status && (
                        <Tag>{record.corrective_action_status}</Tag>
                    )}
                </div>
            )}

            {/* Verifier notes input */}
            <Divider style={{ margin:'16px 0 12px' }}/>
            <div style={{ fontSize:12, fontWeight:700, color:'#555', marginBottom:6 }}>
                Verifier Notes (optional)
            </div>
            <TextArea
                rows={3}
                placeholder="Add notes about your verification decision..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
                style={{ marginBottom:16 }}
            />

            {/* Action buttons */}
            {record.verification_status === 'pending' && (
                <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button danger icon={<CloseCircleOutlined/>}
                        loading={saving} onClick={() => handleVerify('rejected')}>
                        Reject — Return for Revision
                    </Button>
                    <Button type="primary" icon={<CheckCircleOutlined/>}
                        loading={saving} onClick={() => handleVerify('verified')}
                        style={{ background:'#52c41a', borderColor:'#52c41a' }}>
                        Verify &amp; Approve
                    </Button>
                </div>
            )}
            {record.verification_status !== 'pending' && (
                <div style={{ display:'flex', justifyContent:'flex-end' }}>
                    <Button onClick={onClose}>Close</Button>
                </div>
            )}
        </Modal>
    );
}

// ── Main Page ─────────────────────────────────────────────────────────────
export default function MeVerification() {
    const [records,      setRecords]      = useState([]);
    // Permission checks
    const canAdd    = util.canAdd('me_verify');
    const canEdit   = util.canEdit('me_verify');
    const canDelete = util.canDelete('me_verify');
    const canExport = util.canExport('me_verify');
    const [frameworks,   setFrameworks]   = useState([]);
    const [periods,      setPeriods]      = useState([]);
    const [loading,      setLoading]      = useState(true);
    const [viewRecord,   setViewRecord]   = useState(null);
    const [filterFw,     setFilterFw]     = useState('');
    const [filterPeriod, setFilterPeriod] = useState('');
    const [filterVerify, setFilterVerify] = useState('pending');
    const [filterStatus, setFilterStatus] = useState('');
    const [search,       setSearch]       = useState('');
    const [selected,     setSelected]     = useState([]);
    const [bulkNotes,    setBulkNotes]    = useState('');
    const [bulkModal,    setBulkModal]    = useState(false);
    const [bulkAction,   setBulkAction]   = useState('');
    const [saving,       setSaving]       = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [pRes, fwRes, perRes] = await Promise.all([
                MeService.getProgress({
                    ...(filterFw     ? { framework_id:        filterFw }     : {}),
                    ...(filterPeriod ? { reporting_period:    filterPeriod } : {}),
                    ...(filterVerify ? { verification_status: filterVerify } : {}),
                    ...(filterStatus ? { status:              filterStatus } : {}),
                }),
                MeService.getFrameworks({ status:'active' }),
                MeService.getMePeriods(),
            ]);
            setRecords(pRes.data?.data   || []);
            setFrameworks(fwRes.data?.data || []);
            setPeriods(perRes.data?.data  || []);
        } catch(e) { message.error('Failed to load records'); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [filterFw, filterPeriod, filterVerify, filterStatus]); // eslint-disable-line

    const handleVerify = async (progressId, status, notes) => {
        try {
            const r = await MeService.verifyProgress(progressId, {
                verification_status: status,
                verifier_notes:      notes,
            });
            if (r.data?.code !== 200) throw new Error(r.data?.message);
            message.success(`Record ${status}`);
            setSelected([]);
            load();
        } catch(e) { message.error(e.message); throw e; }
    };

    const handleBulkVerify = async () => {
        if (!selected.length) return;
        setSaving(true);
        try {
            const r = await MeService.bulkVerify({
                ids:                 selected,
                verification_status: bulkAction,
                notes:               bulkNotes,
            });
            if (r.data?.code !== 200) throw new Error(r.data?.message);
            message.success(`${selected.length} records ${bulkAction}`);
            setSelected([]);
            setBulkModal(false);
            setBulkNotes('');
            load();
        } catch(e) { message.error(e.message); }
        finally { setSaving(false); }
    };

    // Stats
    const total    = records.length;
    const pending  = records.filter(r => r.verification_status === 'pending').length;
    const verified = records.filter(r => r.verification_status === 'verified').length;
    const rejected = records.filter(r => r.verification_status === 'rejected').length;

    const filtered = records.filter(r =>
        !search ||
        (r.facility_name    || '').toLowerCase().includes(search.toLowerCase()) ||
        (r.indicator_title  || '').toLowerCase().includes(search.toLowerCase()) ||
        (r.framework_name   || '').toLowerCase().includes(search.toLowerCase())
    );

    const columns = [
        { title:'Framework', dataIndex:'framework_name', ellipsis:true,
            render:(v, rec) => (
                <div>
                    <div style={{ fontWeight:700, color:'#1f3864', fontSize:12 }}>{v}</div>
                    <div style={{ fontSize:11, color:'#888' }}>{rec.reporting_period}</div>
                </div>
            )
        },
        { title:'Indicator', dataIndex:'indicator_title', ellipsis:true,
            render:(v, rec) => (
                <div>
                    {rec.indicator_code && (
                        <span style={{ fontSize:11, fontWeight:700, color:'#888', marginRight:4 }}>
                            {rec.indicator_code}
                        </span>
                    )}
                    <span style={{ fontSize:12 }}>{v}</span>
                    {rec.is_critical == 1 && (
                        <Tag color="volcano" style={{ fontSize:10, marginLeft:4 }}>Critical</Tag>
                    )}
                </div>
            )
        },
        { title:'Facility', dataIndex:'facility_name', width:160, ellipsis:true,
            render:(v, rec) => (
                <div>
                    <div style={{ fontSize:12, fontWeight:600 }}>{v}</div>
                    {rec.section_title && (
                        <div style={{ fontSize:11, color:'#888' }}>{rec.section_title}</div>
                    )}
                </div>
            )
        },
        { title:'Status', dataIndex:'progress_status', width:130,
            render: v => {
                const s = PROGRESS_STATUS_CONFIG[v] || PROGRESS_STATUS_CONFIG.not_started;
                return <Badge status={s.color} text={s.label}/>;
            }
        },
        { title:'Achievement', dataIndex:'achievement_pct', width:110, align:'center',
            render: v => v != null
                ? <Progress size="small" percent={parseFloat(v)}
                    format={p => <span style={{ fontSize:10 }}>{p}%</span>}
                    strokeColor={parseFloat(v)>=75?'#52c41a':parseFloat(v)>=50?'#1890ff':'#f5222d'}/>
                : <span style={{ color:'#ccc', fontSize:11 }}>—</span>
        },
        { title:'Reported', dataIndex:'reported_by_name', width:120,
            render:(v, rec) => (
                <div style={{ fontSize:11 }}>
                    <div>{v || '—'}</div>
                    {rec.reported_date && (
                        <div style={{ color:'#888' }}>{moment(rec.reported_date).format('DD MMM YY')}</div>
                    )}
                </div>
            )
        },
        { title:'Verification', dataIndex:'verification_status', width:110,
            render: v => {
                const vs = VERIFY_STATUS_CONFIG[v] || VERIFY_STATUS_CONFIG.pending;
                return <Tag color={vs.color}>{vs.label}</Tag>;
            }
        },
        { title:'Actions', width:100,
            render:(_, rec) => (
                <Space size="small">
                    <Tooltip title="View & Verify">
                        <Button size="small" icon={<EyeOutlined/>}
                            type={rec.verification_status==='pending'?'primary':'default'}
                            style={rec.verification_status==='pending'
                                ? { background:'#1f3864', borderColor:'#1f3864' } : {}}
                            onClick={() => setViewRecord(rec)}/>
                    </Tooltip>
                    {rec.verification_status === 'pending' && (
                        <>
                            <Tooltip title="Quick Verify">
                                <Button size="small" icon={<CheckCircleOutlined/>}
                                    style={{ color:'#52c41a', borderColor:'#52c41a' }}
                                    onClick={() => handleVerify(rec.progress_id, 'verified', '')}/>
                            </Tooltip>
                            <Tooltip title="Quick Reject">
                                <Button size="small" icon={<CloseCircleOutlined/>} danger
                                    onClick={() => handleVerify(rec.progress_id, 'rejected', '')}/>
                            </Tooltip>
                        </>
                    )}
                </Space>
            )
        },
    ];

    return (
        <div className="page-content">
            <div className="page-head d-flex">
                <div className="my-auto">
                    <h4 style={{ margin:0 }}>✅ Verify Progress</h4>
                    <small className="text-muted">Review and sign off on reported M&E progress</small>
                </div>
                <div className="my-auto ml-auto" style={{ display:'flex', gap:8 }}>
                    {selected.length > 0 && (
                        <>
                            <Button icon={<CheckCircleOutlined/>}
                                style={{ color:'#52c41a', borderColor:'#52c41a' }}
                                onClick={() => { setBulkAction('verified'); setBulkModal(true); }}>
                                Verify Selected ({selected.length})
                            </Button>
                            <Button danger icon={<CloseCircleOutlined/>}
                                onClick={() => { setBulkAction('rejected'); setBulkModal(true); }}>
                                Reject Selected ({selected.length})
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <div className="page-pad">
                {/* KPI strip */}
                <Row gutter={12} style={{ marginBottom:16 }}>
                    {[
                        { label:'Total Records', val:total,    color:'#1f3864' },
                        { label:'Pending',       val:pending,  color:'#fa8c16' },
                        { label:'Verified',      val:verified, color:'#52c41a' },
                        { label:'Rejected',      val:rejected, color:'#f5222d' },
                    ].map(k => (
                        <Col key={k.label} xs={12} sm={6}>
                            <Card size="small" style={{ textAlign:'center', borderLeft:`4px solid ${k.color}`,
                                cursor: k.label !== 'Total Records' ? 'pointer' : 'default' }}
                                onClick={() => {
                                    if (k.label === 'Pending')  setFilterVerify('pending');
                                    if (k.label === 'Verified') setFilterVerify('verified');
                                    if (k.label === 'Rejected') setFilterVerify('rejected');
                                }}>
                                <Statistic title={k.label} value={k.val}
                                    valueStyle={{ color:k.color, fontSize:20, fontWeight:800 }}/>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* Filters */}
                <Card size="small" style={{ marginBottom:16 }}>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                        <Input.Search
                            placeholder="Search facility, indicator, framework..."
                            value={search} onChange={e => setSearch(e.target.value)}
                            style={{ width:260 }} allowClear/>
                        <Select value={filterFw} onChange={setFilterFw}
                            style={{ width:200 }} allowClear placeholder="All frameworks">
                            {frameworks.map(f => (
                                <Option key={f.id} value={f.id}>{f.code} — {f.name}</Option>
                            ))}
                        </Select>
                        <Select value={filterPeriod} onChange={setFilterPeriod}
                            style={{ width:160 }} allowClear placeholder="All periods">
                            {periods.map(p => (
                                <Option key={p.label} value={p.label}>{p.label}</Option>
                            ))}
                        </Select>
                        <Select value={filterVerify} onChange={setFilterVerify}
                            style={{ width:140 }} allowClear placeholder="All verification">
                            <Option value="pending">Pending</Option>
                            <Option value="verified">Verified</Option>
                            <Option value="rejected">Rejected</Option>
                        </Select>
                        <Select value={filterStatus} onChange={setFilterStatus}
                            style={{ width:150 }} allowClear placeholder="All progress statuses">
                            {Object.entries(PROGRESS_STATUS_CONFIG).map(([val, cfg]) => (
                                <Option key={val} value={val}>
                                    <Badge status={cfg.color} text={cfg.label}/>
                                </Option>
                            ))}
                        </Select>
                        <Tag style={{ marginLeft:'auto' }}>
                            {filtered.length} record{filtered.length !== 1 ? 's' : ''}
                        </Tag>
                    </div>
                </Card>

                {/* Pending alert */}
                {pending > 0 && filterVerify === 'pending' && (
                    <Alert type="warning" showIcon style={{ marginBottom:12 }}
                        message={`${pending} progress record${pending>1?'s':''} awaiting verification`}
                        description="Use the checkboxes to select multiple records for bulk verification."
                    />
                )}

                {/* Table */}
                <Card size="small">
                    <Table
                        size="small"
                        dataSource={filtered}
                        rowKey="progress_id"
                        columns={columns}
                        loading={loading}
                        rowSelection={{
                            selectedRowKeys: selected,
                            onChange: setSelected,
                            getCheckboxProps: rec => ({
                                disabled: rec.verification_status !== 'pending',
                            }),
                        }}
                        pagination={{
                            pageSize:20,
                            showSizeChanger:true,
                            showTotal: t => `${t} records`,
                        }}
                        rowClassName={rec =>
                            rec.verification_status === 'pending' ? '' :
                            rec.verification_status === 'verified' ? 'ant-table-row-selected' : ''
                        }
                        expandable={{
                            expandedRowRender: rec => (
                                <div style={{ padding:'8px 16px', fontSize:12 }}>
                                    {rec.progress_narrative && (
                                        <div style={{ marginBottom:6 }}>
                                            <strong>Narrative: </strong>{rec.progress_narrative}
                                        </div>
                                    )}
                                    {rec.evidence_description && (
                                        <div style={{ marginBottom:6 }}>
                                            <strong>Evidence: </strong>{rec.evidence_description}
                                            {rec.evidence_reference && ` (Ref: ${rec.evidence_reference})`}
                                        </div>
                                    )}
                                    {rec.gap_identified && (
                                        <div style={{ color:'#f5222d' }}>
                                            <strong>Gap: </strong>{rec.gap_identified}
                                        </div>
                                    )}
                                    {rec.verifier_notes && (
                                        <div style={{ marginTop:6, color:'#7c3aed' }}>
                                            <strong>Verifier Notes: </strong>{rec.verifier_notes}
                                        </div>
                                    )}
                                </div>
                            ),
                        }}
                    />
                </Card>
            </div>

            {/* Detail + verify modal */}
            <ProgressDetailModal
                visible={!!viewRecord}
                onClose={() => setViewRecord(null)}
                record={viewRecord}
                onVerify={handleVerify}
                onReject={handleVerify}
            />

            {/* Bulk action modal */}
            <Modal
                title={<span style={{ fontWeight:800 }}>
                    {bulkAction === 'verified'
                        ? `✅ Verify ${selected.length} Records`
                        : `❌ Reject ${selected.length} Records`}
                </span>}
                open={bulkModal}
                onCancel={() => { setBulkModal(false); setBulkNotes(''); }}
                footer={[
                    <Button key="cancel" onClick={() => { setBulkModal(false); setBulkNotes(''); }}>
                        Cancel
                    </Button>,
                    <Button key="ok" type="primary" loading={saving}
                        danger={bulkAction === 'rejected'}
                        style={bulkAction === 'verified'
                            ? { background:'#52c41a', borderColor:'#52c41a' } : {}}
                        onClick={handleBulkVerify}>
                        Confirm {bulkAction === 'verified' ? 'Verification' : 'Rejection'}
                    </Button>,
                ]}
            >
                <div style={{ marginBottom:12, fontSize:13, color:'#555' }}>
                    You are about to <strong>{bulkAction}</strong> {selected.length} progress record{selected.length>1?'s':''}.
                    This action will be logged against your account.
                </div>
                <TextArea
                    rows={3}
                    placeholder="Add notes for all selected records (optional)..."
                    value={bulkNotes}
                    onChange={e => setBulkNotes(e.target.value)}
                />
            </Modal>
        </div>
    );
}


